import React from 'react';

import { TrackableItem, TrackableList } from 'types/trackables';

import { useTrackableLists, useTrackableItems } from 'trackables/hooks';
import { useTrackableFiles } from 'trackables/hooks/useTrackableFiles';

import { Field } from 'components/Field/Field';
import { Select } from 'components/Select/Select';
import { Pill } from 'components/Pill/Pill';
import { Text } from 'components/Text/Text';

type WatchlistItemPickerProps = {
  onSelect: (imageUrl: string) => void;
};

export function WatchlistItemPicker({ onSelect }: WatchlistItemPickerProps) {
  const [selectedList, setSelectedList] = React.useState<TrackableList | null>(
    null
  );
  const [selectedItem, setSelectedItem] = React.useState<TrackableItem | null>(
    null
  );

  const { data: faceListsData, isLoading: isLoadingLists } = useTrackableLists({
    params: { object_types: ['face'], limit: 100 },
  });

  const faceLists = React.useMemo(
    () => faceListsData?.data ?? [],
    [faceListsData]
  );

  const { data: itemsData, isLoading: isLoadingItems } = useTrackableItems({
    params: {
      trackable_list_id: selectedList?.id,
      limit: 100,
    },
    enabled: Boolean(selectedList?.id),
  });

  const items = React.useMemo(() => itemsData?.data ?? [], [itemsData]);

  const { data: filesData, isLoading: isLoadingFiles } = useTrackableFiles({
    params: {
      trackable_list_id: selectedList?.id ?? '',
      trackable_item_id: selectedItem?.id ?? '',
      with_thumbnail: true,
      limit: 20,
    },
    enabled: Boolean(selectedList?.id && selectedItem?.id),
  });

  const files = React.useMemo(() => filesData?.data ?? [], [filesData]);

  if (isLoadingLists || faceLists.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <Field label="Watchlist">
        <Select<TrackableList>
          options={faceLists}
          value={selectedList}
          onChange={(value) => {
            setSelectedList(value as TrackableList | null);
            setSelectedItem(null);
          }}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
          isLoading={isLoadingLists}
          placeholder="Select a face watchlist..."
        />
      </Field>

      {selectedList && (
        <Field label="Item">
          <Select<TrackableItem>
            options={items}
            value={selectedItem}
            onChange={(value) =>
              setSelectedItem(value as TrackableItem | null)
            }
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            formatOptionLabel={(option, { context }) => {
              if (context === 'value') {
                return <span>{option.name}</span>;
              }
              return (
                <div className="flex flex-col gap-0.5 py-0.5">
                  <span className="text-sm font-medium">{option.name}</span>
                  {option.description && (
                    <span className="text-xs opacity-60">
                      {option.description}
                    </span>
                  )}
                  {(option.labels?.length ?? 0) > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {option.labels.map((label) => (
                        <Pill key={label} size="small">
                          {label}
                        </Pill>
                      ))}
                    </div>
                  )}
                </div>
              );
            }}
            isLoading={isLoadingItems}
            placeholder="Select an item..."
          />
        </Field>
      )}

      {selectedItem && (
        <ReferenceFaceGrid
          files={files}
          isLoading={isLoadingFiles}
          onSelect={onSelect}
        />
      )}
    </div>
  );
}

type ReferenceFaceGridProps = {
  files: {
    id: string;
    name: string;
    data_url?: string | null;
    thumbnail_url?: string | null;
  }[];
  isLoading: boolean;
  onSelect: (imageUrl: string) => void;
};

function ReferenceFaceGrid({
  files,
  isLoading,
  onSelect,
}: ReferenceFaceGridProps) {
  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <Text size="small" className="opacity-50">
        Loading...
      </Text>
    );
  } else if (files.length === 0) {
    body = (
      <Text size="small" className="opacity-50">
        No reference faces.
      </Text>
    );
  } else {
    body = (
      <div className="flex flex-wrap gap-2">
        {files.map((file) => {
          const url = file.data_url ?? file.thumbnail_url;
          if (!url) return null;
          return (
            <button
              key={file.id}
              type="button"
              onClick={() => onSelect(url)}
              className="h-[72px] w-[72px] cursor-pointer overflow-hidden rounded-md border-2 border-neutral-300 bg-transparent p-0 transition-all hover:border-blue-500 hover:shadow-md dark:border-neutral-700 dark:hover:border-blue-400"
              title={`Search with ${file.name}`}
            >
              <img
                src={url}
                alt={file.name}
                crossOrigin="anonymous"
                className="block h-full w-full object-cover"
              />
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <Text size="small" className="opacity-60">
        Click a reference face to search
      </Text>
      {body}
    </div>
  );
}
